/**
 * Règles métier — moteur de règles déclaratif
 * Billing, prescriptions, interactions, age_rules, pregnancy_rules, duplicate_rules, device_rules, claim_rules
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Wand2, Plus, Search, Code, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

interface Rule {
  id: string;
  name: string;
  category: "billing" | "prescription" | "interaction" | "age" | "pregnancy" | "duplicate" | "device" | "claim";
  description: string;
  condition: string;
  effect: "ALLOW" | "DENY" | "WARN" | "REQUIRE_APPROVAL";
  severity: "INFO" | "WARNING" | "CRITICAL";
  enabled: boolean;
}

const RULES: Rule[] = [
  // Billing
  { id: "r-b01", name: "Facture > 10 000 MAD nécessite approbation", category: "billing", description: "Toute facture dépassant 10 000 MAD doit être validée par le chef de service", condition: "invoice.total > 10000 AND role != 'ADMIN'", effect: "REQUIRE_APPROVAL", severity: "WARNING", enabled: true },
  { id: "r-b02", name: "ALD = ticket modérateur 0", category: "billing", description: "Si patient en ALD, le ticket modérateur est automatiquement 0", condition: "patient.has_ald == true", effect: "ALLOW", severity: "INFO", enabled: true },
  { id: "r-b03", name: "Plafond journalier biologie", category: "billing", description: "Maximum 5 actes B par jour et par patient (cumul)", condition: "count(acts where lettrage == 'B', day) > 5", effect: "DENY", severity: "WARNING", enabled: true },
  { id: "r-b04", name: "Consultation non cumulable avec visite le même jour", category: "billing", description: "C et V ne peuvent pas être cumulés le même jour pour le même patient", condition: "act.code IN ('C','CS','CSC') AND other_act IN ('V','VL')", effect: "DENY", severity: "CRITICAL", enabled: true },
  // Prescription
  { id: "r-p01", name: "Stupéfiants : ordonnance sécurisée obligatoire", category: "prescription", description: "Toute prescription de stupéfiant doit être sur ordonnance sécurisée (papier)", condition: "drug.controlled == true", effect: "REQUIRE_APPROVAL", severity: "CRITICAL", enabled: true },
  { id: "r-p02", name: "Antibiotique : durée max 14 jours", category: "prescription", description: "La durée d'une antibiothérapie ne peut excéder 14 jours sans renouvellement spécialisé", condition: "drug.atc.startsWith('J01') AND duration > 14", effect: "WARN", severity: "WARNING", enabled: true },
  { id: "r-p03", name: "Benzodiazépine : durée max 12 semaines", category: "prescription", description: "Conformément aux recommandations ANSM, durée limitée à 12 semaines", condition: "drug.atc.startsWith('N05BA') AND duration_weeks > 12", effect: "WARN", severity: "WARNING", enabled: true },
  // Interactions
  { id: "r-i01", name: "AVK + AINS : interaction majeure", category: "interaction", description: "Association Warfarine + Ibuprofène/Aspirine augmente le risque hémorragique", condition: "drug1.atc == 'B01AA03' AND drug2.atc IN ('M01AE','B01AC06')", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-i02", name: "IEC + AINS : interaction modérée", category: "interaction", description: "Association IEC + AINS : risque d'insuffisance rénale aiguë", condition: "drug1.atc.startsWith('C09') AND drug2.atc.startsWith('M01A')", effect: "WARN", severity: "WARNING", enabled: true },
  { id: "r-i03", name: "Méthotrexate + AINS : contre-indication", category: "interaction", description: "Toxicité hématologique du méthotrexate augmentée par les AINS", condition: "drug1.atc == 'L01BA01' AND drug2.atc.startsWith('M01A')", effect: "DENY", severity: "CRITICAL", enabled: true },
  // Age
  { id: "r-a01", name: "Aspirine contre-indiquée < 12 ans (Reye)", category: "age", description: "Risque de syndrome de Reye chez l'enfant", condition: "drug.atc == 'B01AC06' AND patient.age < 12", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-a02", name: "Tétracyclines contre-indiquées < 8 ans", category: "age", description: "Coloration dentaire et troubles de la croissance osseuse", condition: "drug.atc.startsWith('J01AA') AND patient.age < 8", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-a03", name: "Codéine contre-indiquée < 12 ans", category: "age", description: "Risque de dépression respiratoire post-opératoire", condition: "drug.atc == 'R05DA04' AND patient.age < 12", effect: "DENY", severity: "CRITICAL", enabled: true },
  // Pregnancy
  { id: "r-pr01", name: "Isotrétinoïne : TÉRATOGÈNE, contraception obligatoire", category: "pregnancy", description: "Programme de prévention de la grossesse obligatoire (2 méthodes contraceptives + tests)", condition: "drug.atc == 'D10BA01' AND patient.gender == 'F'", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-pr02", name: "AINS contre-indiqués 3ème trimestre", category: "pregnancy", description: "Risque de fermeture du canal artériel et insuffisance rénale fœtale", condition: "drug.atc.startsWith('M01A') AND patient.gender == 'F' AND pregnancy.trimester == 3", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-pr03", name: "Metformine : utilisable pendant la grossesse (recommandations 2020)", category: "pregnancy", description: "Metformine peut être poursuivie pendant la grossesse", condition: "drug.atc == 'A10BA02' AND pregnancy == true", effect: "ALLOW", severity: "INFO", enabled: true },
  // Duplicate
  { id: "r-d01", name: "Détection doublons prescription", category: "duplicate", description: "Alerte si le même DCI est déjà prescrit dans les 7 derniers jours", condition: "drug.inn == active_prescription.drug.inn AND days_since(active_prescription) < 7", effect: "WARN", severity: "WARNING", enabled: true },
  { id: "r-d02", name: "Détection doublons biologie", category: "duplicate", description: "Empêche la prescription du même acte B dans les 24h", condition: "act.code == previous_act.code AND days_since(previous_act) < 1", effect: "DENY", severity: "WARNING", enabled: true },
  // Device
  { id: "r-dv01", name: "IRM contre-indiquée avec pacemaker non IRM-compatible", category: "device", description: "Risque d'échauffement et de dysfonction du dispositif", condition: "device.class == 'implant' AND device.mri_compatible == false AND exam == 'IRM'", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-dv02", name: "Vérification maintenance dispositif avant utilisation", category: "device", description: "Pas d'utilisation si maintenance > 6 mois ou status != ACTIVE", condition: "device.last_maintenance > 180 OR device.status != 'ACTIVE'", effect: "WARN", severity: "WARNING", enabled: true },
  // Claim
  { id: "r-c01", name: "FSE doit être signée avant soumission CNSS", category: "claim", description: "Pas de soumission sans signature électronique du praticien", condition: "claim.signed == false", effect: "DENY", severity: "CRITICAL", enabled: true },
  { id: "r-c02", name: "Plafond mensuel FSE par praticien", category: "claim", description: "Maximum 200 FSE par mois par praticien (anti-fraude)", condition: "count(claims by doctor_id, month) > 200", effect: "REQUIRE_APPROVAL", severity: "WARNING", enabled: true },
];

const CATEGORY_LABEL: Record<Rule["category"], string> = {
  billing: "Facturation",
  prescription: "Prescription",
  interaction: "Interactions",
  age: "Âge",
  pregnancy: "Grossesse",
  duplicate: "Doublons",
  device: "Dispositifs",
  claim: "Feuilles de soins",
};

const EFFECT_TONE: Record<Rule["effect"], "default" | "secondary" | "destructive" | "outline"> = {
  ALLOW: "secondary",
  DENY: "destructive",
  WARN: "outline",
  REQUIRE_APPROVAL: "outline",
};

const SEVERITY_TONE: Record<Rule["severity"], "default" | "secondary" | "destructive" | "outline"> = {
  INFO: "outline",
  WARNING: "secondary",
  CRITICAL: "destructive",
};

export const Route = createFileRoute("/referential/rules")({
  component: RulesEngine,
});

function RulesEngine() {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [showDisabled, setShowDisabled] = useState(false);

  const filtered = useMemo(() => RULES.filter((r) => {
    if (!showDisabled && !r.enabled) return false;
    if (filterCat !== "all" && r.category !== filterCat) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return r.name.toLowerCase().includes(s) || r.description.toLowerCase().includes(s) || r.condition.toLowerCase().includes(s);
  }), [search, filterCat, showDisabled]);

  const categories = Array.from(new Set(RULES.map((r) => r.category)));
  const stats = useMemo(() => ({
    total: RULES.length,
    enabled: RULES.filter((r) => r.enabled).length,
    critical: RULES.filter((r) => r.severity === "CRITICAL").length,
    blocking: RULES.filter((r) => r.effect === "DENY").length,
  }), []);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Wand2 className="h-5 w-5" /> Règles métier
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Moteur déclaratif · {RULES.length} règles · {categories.length} catégories · Évaluation synchrone à chaque action
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Total règles</div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Actives</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{stats.enabled}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Critiques</div>
            <div className="mt-1 text-2xl font-bold text-red-400">{stats.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Bloquantes</div>
            <div className="mt-1 text-2xl font-bold text-amber-400">{stats.blocking}</div>
            <div className="text-xs text-muted-foreground">DENY</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (nom, description, condition)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button size="sm" variant={showDisabled ? "default" : "outline"} onClick={() => setShowDisabled(!showDisabled)}>
          {showDisabled ? "Toutes" : "Actives seulement"}
        </Button>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <Button size="sm" variant={filterCat === "all" ? "default" : "outline"} onClick={() => setFilterCat("all")}>
          Toutes
        </Button>
        {categories.map((c) => (
          <Button key={c} size="sm" variant={filterCat === c ? "default" : "outline"} onClick={() => setFilterCat(c)}>
            {CATEGORY_LABEL[c as Rule["category"]]} ({RULES.filter((r) => r.category === c).length})
          </Button>
        ))}
      </div>

      <div className="space-y-1.5">
        {filtered.map((r) => (
          <Card key={r.id} className={!r.enabled ? "opacity-50" : ""}>
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {r.effect === "ALLOW" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> :
                    r.effect === "DENY" ? <XCircle className="h-4 w-4 text-red-400" /> :
                      r.effect === "WARN" ? <AlertTriangle className="h-4 w-4 text-amber-400" /> :
                        <AlertTriangle className="h-4 w-4 text-blue-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm">{r.name}</span>
                    <Badge variant="outline" className="text-[9px]">{CATEGORY_LABEL[r.category]}</Badge>
                    <Badge variant={EFFECT_TONE[r.effect]} className="text-[9px]">{r.effect}</Badge>
                    <Badge variant={SEVERITY_TONE[r.severity]} className="text-[9px]">{r.severity}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{r.description}</div>
                  <div className="mt-1 flex items-start gap-1.5 text-[11px]">
                    <Code className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                    <code className="bg-white/5 px-1.5 py-0.5 rounded font-mono text-[10px]">{r.condition}</code>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
            Aucune règle ne correspond
          </div>
        )}
      </div>
    </div>
  );
}
